import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';

import { RecordingsService } from '../../services/recordings/recordings.service';
import { RecordingMenuComponent } from '../../menus/recording-menu/recording-menu.component';
import { Recording, RecordingStats } from '@sc/types';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { RoleLimits } from '@sc/types';
import { RolesService } from '../../services/roles/roles.service';
import { RecordingTags, StatsType } from '@sc/types';
import { Subscription } from 'rxjs';
import { RecordingService } from '../../services/recording/recording.service';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { StatsService } from '../../services/stats/stats.service';

@Component({
  selector: 'sc-recording-list-item',
  templateUrl: './recording-list-item.component.html',
  styleUrls: ['./recording-list-item.component.scss'],
})
export class RecordingListItemComponent implements OnInit, OnDestroy {
  @Input() recordingID: string;
  @Input() sessionID: string;
  @Input() last: boolean;
  @Input() appearance: 'preview' | 'take' | 'dashboard';
  recording: Recording;
  role$ = this.rolesService.role$;
  roleLimits = RoleLimits;
  recordingsTag = RecordingTags;
  isMixed: boolean;
  isEnhanced: boolean;
  isScreen: boolean;
  isMigrated: boolean;
  recordingTag: number;
  audioStats: RecordingStats;
  videoStats: RecordingStats;
  screenStats: RecordingStats;
  audioUploadProgress = 0;
  videoUploadProgress = 0;
  screenUploadProgress = 0;
  subs: Array<Subscription> = [];

  constructor(
    private popoverController: PopoverController,
    private recordingService: RecordingService,
    private recordingsService: RecordingsService,
    private statsService: StatsService,
    private analyticsService: AnalyticsService,
    private rolesService: RolesService
  ) {}

  async ngOnInit() {
    this.setupRecording();
    this.setupWorkflow();
  }

  async setupRecording() {
    const sub = this.recordingService.getRecording(this.recordingID, this.sessionID).subscribe((recording) => {
      if (!recording) return;
      const recData = {
        ...recording,
        audioPlaybackVisible: this.recording?.audioPlaybackVisible ?? false,
        videoPlaybackVisible: this.recording?.videoPlaybackVisible ?? false,
        isChecked:
          !!this.recordingsService.selectedRecordings$.value.find((rec) => rec.recordingID === recording.recordingID) ??
          false,
      };
      if (!this.recording) {
        this.setupStats(recording);
      }
      this.recording = recData;
      this.setupTags();
    });
    this.subs.push(sub);
  }

  setupStats(recording: Recording) {
    if (recording.screenRecording) {
      const screenSub = this.statsService
        .getRecordingStats(this.recordingID, StatsType.SCREEN, this.sessionID)
        .subscribe((stats) => {
          this.screenStats = stats;
          if (stats?.totalChunks) this.screenUploadProgress = 1 - stats.chunkQueueSize / stats.totalChunks;
        });
      this.subs.push(screenSub);
    } else {
      const audioSub = this.statsService
        .getRecordingStats(this.recordingID, StatsType.AUDIO, this.sessionID)
        .subscribe((stats) => {
          this.audioStats = stats;
          if (stats?.totalChunks) this.audioUploadProgress = 1 - stats.chunkQueueSize / stats.totalChunks;
        });
      this.subs.push(audioSub);

      const videoSub = this.statsService
        .getRecordingStats(this.recordingID, StatsType.VIDEO, this.sessionID)
        .subscribe((stats) => {
          this.videoStats = stats;
          if (stats?.totalChunks) this.videoUploadProgress = 1 - stats.chunkQueueSize / stats.totalChunks;
        });
      this.subs.push(videoSub);
    }
  }

  setupWorkflow() {
    const sub = this.recordingsService.selectedRecordings$.subscribe((selectedRecordings) => {
      const selected = selectedRecordings.find((rec) => rec.recordingID === this.recordingID);
      if (!this.recording) return;
      this.recording.isChecked = !!selected;
    });

    this.subs.push(sub);
  }

  async setupTags() {
    this.isMixed = this.recording.fileName ? this.recording.fileName.includes('mix_') : false;
    this.isEnhanced = this.recording.fileName ? this.recording.fileName.includes('enhanced_') : this.recording.enhanced;
    this.isScreen = this.recording.screenRecording;
    this.isMigrated = this.recording.migrated;

    if (this.isMixed) {
      this.recordingTag = RecordingTags.MIXED;
    } else if (this.isEnhanced) {
      this.recordingTag = RecordingTags.MASTERED;
    } else if (this.isScreen) {
      this.recordingTag = RecordingTags.SCREEN;
    } else {
      this.recordingTag = null;
    }
  }

  async openRecordingMenu(recording: Recording, event: MouseEvent) {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: RecordingMenuComponent,
      componentProps: { recording, event },
      event,
    });
    await menu.present();
    await this.analyticsService.track('opened manage recording', {
      recordingID: recording.recordingID,
      sessionID: recording.sessionID,
      showID: recording.showID,
    });
  }

  async togglePlaybackVisible(type: 'audio' | 'video', recording: Recording) {
    if (type === 'audio') {
      recording.audioPlaybackVisible = !recording.audioPlaybackVisible;

      if (recording.audioPlaybackVisible && recording.videoPlaybackVisible) {
        recording.videoPlaybackVisible = false;
      }
    }

    if (type === 'video') {
      recording.videoPlaybackVisible = !recording.videoPlaybackVisible;

      if (recording.videoPlaybackVisible && recording.audioPlaybackVisible) {
        recording.audioPlaybackVisible = false;
      }
    }

    await this.analyticsService.track('toggled playback', {
      type,
      recordingID: recording.recordingID,
      sessionID: recording.sessionID,
      showID: recording.showID,
    });
  }

  async checked(event: any) {
    if (event.detail.checked) {
      const recording: Recording = { ...this.recording };
      this.recordingsService.addSelectedRecording(recording);
    } else {
      const recording: Recording = { ...this.recording };
      this.recordingsService.removeSelectedRecording(recording);
    }

    await this.analyticsService.track(`toggled recording checkbox`, {
      enabled: event.detail.checked,
      recordingID: this.recording.recordingID,
    });
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
